/* eslint-disable no-nested-ternary */
import React, { memo } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Typography, InputBase, InputBaseProps } from '@quillbot/design-system';
import { getCgBreakpointDown } from 'src/citation-generator/utils';
import { useRouter } from 'next/router';
import { useEditFormDispatch, useEditFormState } from '../context';
import { FormValueKeys } from '../types';
import { useFieldConfig, useFieldValidator } from '../config';

interface TextWithLabelStyleProps {
  error?: boolean;
  isHighlighted?: boolean;
  isCoWriter?: boolean;
}

const useTextWithLabelStyle = makeStyles((theme) => ({
  baseRoot: {
    [getCgBreakpointDown(theme)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  inputContainer: {
    border: ({ error, isHighlighted }: TextWithLabelStyleProps) =>
      `1px solid ${
        error
          ? theme.palette.error.main
          : isHighlighted
          ? theme.palette.secondary.main
          : theme.palette.divider
      }`,
    borderRadius: 3,
    width: '76%',
    height: '30px',
    [getCgBreakpointDown(theme)]: {
      width: '100%',
      marginTop: 4,
    },
  },
  input: {
    padding: '0px 10px',
    fontSize: '13px',
    fontWeight: 600,
    width: '100%',
    color: theme.palette.text.primary,
  },
  label: {
    float: 'right',
    fontSize: '12px',
    color: theme.palette.text.primary,
    [getCgBreakpointDown(theme)]: {
      float: 'unset',
    },
  },
  labelContainer: {
    width: '24%',
    paddingRight: (props: TextWithLabelStyleProps) => (props.isCoWriter ? '8px' : '10px'),
    [getCgBreakpointDown(theme)]: {
      width: '100%',
    },
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '12px',
    marginTop: 2,
    marginLeft: 'calc(24% + 4px)',
    [getCgBreakpointDown(theme)]: {
      marginLeft: 0,
    },
  },
}));

export function TextFieidWithLabel(props: {
  label: string | JSX.Element;
  inputBaseProps: InputBaseProps;
  rootClass?: any;
  errorMessage?: string;
  isHighlighted?: boolean;
}) {
  const { label, inputBaseProps, rootClass, errorMessage, isHighlighted } = props;
  const classes = useTextWithLabelStyle({ error: Boolean(errorMessage), isHighlighted });

  return (
    <>
      <Grid container alignItems="center" className={`${rootClass} ${classes.baseRoot}`}>
        <Grid item className={classes.labelContainer}>
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid item className={classes.inputContainer}>
          <InputBase className={classes.input} {...inputBaseProps} />
        </Grid>
      </Grid>

      <Grid container>
        {errorMessage && <Typography className={classes.error}>{errorMessage}</Typography>}
      </Grid>
    </>
  );
}

function Field(props: {
  rootClass?: string;
  valueKey: FormValueKeys;
  setter: Function;
  inputProps?: any;
}) {
  const { rootClass, valueKey, setter, inputProps = null } = props;
  const { label, show } = useFieldConfig(valueKey);
  const value = useEditFormState((state) => state[valueKey]);
  const { error: errorMessage, warning } = useFieldValidator(valueKey);
  const router = useRouter();
  const isCoWriter = router.pathname.includes('/co-writer');
  const classes = useTextWithLabelStyle({
    error: Boolean(errorMessage),
    isHighlighted: Boolean(warning),
    isCoWriter,
  });
  const dispatch = useEditFormDispatch();

  if (!show) {
    return null;
  }

  return (
    <>
      <Grid container alignItems="center" className={`${rootClass} ${classes.baseRoot}`}>
        <Grid item className={classes.labelContainer}>
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid item className={classes.inputContainer}>
          <InputBase
            inputProps={{ ...inputProps }}
            className={classes.input}
            value={value || ''}
            onChange={(e) => {
              dispatch(setter(valueKey, e.target.value));
            }}
          />
        </Grid>
      </Grid>

      <Grid container>
        {errorMessage && (
          <Typography className={classes.error}>{`${label}${errorMessage}`}</Typography>
        )}
      </Grid>
    </>
  );
}

export const TextField = memo(Field);
